import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  useTheme,
  Box,
  Avatar,
  AvatarGroup,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Skeleton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Popover,
  InputBase,
  Checkbox,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation } from "react-i18next";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Person, Search, Close } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import BoltIcon from "@mui/icons-material/Bolt";
import { colorPalette, typeToIconMapping } from "../utils";
import Pagination from "@/Components/Common/TablePro/Pagination";
import { useDroppable } from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";
import { useAppSelector } from "@/app/hooks";
import { selectApiData } from "@/features/bughurd/boardSelector";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface BugItem {
  _id: string;
  bugKey: string;
  summary: string;
  assignTo: Array<{
    _id: string;
    fullName: string;
  }>;
  bugStatus: {
    _id: string;
    statusCode: string;
    status: string;
  };
  severity: string;
  type: string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  project: {
    _id: string;
    name: string;
    code: string;
  };
  createdAt: string;
  updatedAt: string;
  subTasks?: Array<{
    _id: string;
    bugKey: string;
    summary: string;
    assignTo: Array<{ _id: string; fullName: string }>;
    bugStatus: { _id: string; statusCode: string; status: string };
    type: string;
    [key: string]: any;
  }>;
  epicTasks?: Array<{
    _id: string;
    bugKey: string;
    summary: string;
    assignTo: Array<{ _id: string; fullName: string }>;
    bugStatus: { _id: string; statusCode: string; status: string };
    type: string;
    [key: string]: any;
  }>;
  epic?: {
    _id: string;
    bugKey: string;
    summary: string;
  };
}

interface TaskAccordionProps {
  accordionKey: string;
  droppableId: string;
  title: string;
  isExpanded: boolean;
  onAccordionChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  tasks: BugItem[];
  totalCount: number;
  isLoading: boolean;
  paginationModel: {
    page: number;
    pageSize: number;
  };
  setPaginationModel: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, taskId: string) => void;
  isCreatingTask: boolean;
  newTaskName: string;
  onCreateTaskClick: () => void;
  onTaskNameChange: (name: string) => void;
  onCreateTask: () => void;
  onCancelTaskCreation: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  isCreatingBug: boolean;
  headerButtons?: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: (event: React.MouseEvent) => void;
    disabled?: boolean;
  }>;
  sprint?: {
    _id: string;
    name: string;
    status: string;
    startDate?: string;
    endDate?: string;
    sprintKey?: string;
    description?: string;
    totalBugCount?: number;
    doneBugCount?: number;
  };
  onDeleteSprint?: (sprintId: string) => void;
  onEditSprint?: (sprintId: string) => void;
  isDeletingSprint?: boolean;
  emptyMessage?: string;
  isDragging?: boolean;
  selectedTasks: Set<string>;
  onSelectionChange: (selection: Set<string>) => void;
  isSelectionDisabled?: boolean;
  onStatusClick?: (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => void;
  editingStatusTaskId?: string | null;
  statusList?: any[];
  isUpdatingStatus?: boolean;
  availableUsers?: any[];
  onTaskUpdate?: (
    taskId: string,
    obj: any,
    field: string,
    containerId?: string
  ) => any;
}

interface SubTasksDetailPanelProps {
  parentTask: BugItem;
  onTaskClick?: (taskId: string) => void;
  renderAssignee: (assignees: any[], taskId: string) => React.ReactNode;
}

const SubTasksDetailPanel: React.FC<SubTasksDetailPanelProps> = ({
  parentTask,
  onTaskClick,
  renderAssignee,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const epicTasks = parentTask.epicTasks || [];
  const subTasks = parentTask.subTasks || [];

  const hasEpicTasks = epicTasks.length > 0;
  const hasSubTasks = subTasks.length > 0;

  const renderTaskRow = (task: any, isEpicTask: boolean) => {
    const type = task?.type || "";
    const typeInfo = type ? typeToIconMapping[type] : typeToIconMapping.TASK;
    const displayText = task?.bugKey
      ? `${task.bugKey}: ${task.summary}`
      : task.summary;

    return (
      <Box
        key={task._id}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        onClick={() => onTaskClick && onTaskClick(task._id)}
      >
        <Box sx={{ width: 40 }} />
        <Box sx={{ flex: 2, minWidth: 200, mr: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {type && (
              <Tooltip title={type} placement="top-start">
                <Box
                  component="span"
                  sx={{
                    width: 14,
                    height: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: typeInfo?.bgcolor,
                    borderRadius: "2px",
                    color: "white",
                    flexShrink: 0,
                    fontSize: "10px",
                  }}
                >
                  {typeInfo.icon}
                </Box>
              </Tooltip>
            )}
            <Tooltip title={displayText} placement="top-start">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                {task?.bugKey && (
                  <span style={{ marginRight: "4px", color: "#1976D2" }}>
                    {task.bugKey}:
                  </span>
                )}
                {task.summary}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ width: 120, mr: 10 }}>
          {renderAssignee(task.assignTo, task._id)}
        </Box>
        <Box sx={{ width: 100, mr: 12 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task.bugStatus?.status}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (!hasEpicTasks && !hasSubTasks) {
    return (
      <Box
        sx={{
          p: 3,
          pl: 8,
          textAlign: "center",
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.grey[25],
        }}
      >
        <Typography variant="body2">
          {t("No linked tasks or sub-tasks found")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        pl: 6,
        backgroundColor: theme.palette.grey[25],
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      {hasEpicTasks && (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              mb: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <BoltIcon fontSize="small" />
            {t("Linked Tasks")} ({epicTasks.length})
          </Typography>
          {epicTasks.map((task) => renderTaskRow(task, true))}
        </Box>
      )}

      {hasSubTasks && (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              mb: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {t("Sub-Tasks")} ({subTasks.length})
          </Typography>
          {subTasks.map((task) => renderTaskRow(task, false))}
        </Box>
      )}
    </Box>
  );
};

interface DraggableTaskRowProps {
  task: BugItem;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, taskId: string) => void;
  isDragging?: boolean;
  isSelected?: boolean;
  selectedCount?: number;
  onToggleSelection: (taskId: string, selected: boolean) => void;
  isSelectionDisabled?: boolean;
  onStatusClick?: (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => void;
  isUpdatingStatus?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (taskId: string) => void;
  renderAssignee: (assignees: any[], taskId: string) => React.ReactNode;
}

const DraggableTaskRow: React.FC<DraggableTaskRowProps> = ({
  task,
  onTaskClick,
  onMenuClick,
  isDragging = false,
  isSelected = false,
  selectedCount = 0,
  onToggleSelection,
  isSelectionDisabled = false,
  onStatusClick,
  isUpdatingStatus = false,
  isExpanded = false,
  onToggleExpand,
  renderAssignee,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const hasEpicTasks = task.epicTasks && task.epicTasks.length > 0;
  const hasSubTasks = task.subTasks && task.subTasks.length > 0;
  const hasExpandableContent = hasEpicTasks || hasSubTasks;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isCurrentTaskDragging,
  } = useDraggable({
    id: task._id,
    data: {
      type: "task",
      task: task,
    },
    disabled: !isSelected,
  });

  const type = task?.type || "";
  const typeInfo = type ? typeToIconMapping[type] : typeToIconMapping.TASK;
  const displayText = task?.bugKey
    ? `${task.bugKey}: ${task.summary}`
    : task.summary;

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
      }
    : undefined;

  if (isCurrentTaskDragging) {
    return <Box sx={{ height: 52, backgroundColor: "transparent" }} />;
  }

  return (
    <>
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 52,
          cursor: isSelected ? "grab" : "default",
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : "transparent",
          opacity: isSelectionDisabled && !isSelected ? 0.6 : 1,
          border: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : "2px solid transparent",
          position: "relative",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            "& .drag-handle": {
              opacity: 1,
            },
          },
          ...(isDragging &&
            !isCurrentTaskDragging && {
              transition: "all 0.2s ease",
            }),
        }}
      >
        <Box sx={{ width: 32, mr: 0.5 }}>
          {hasExpandableContent && onToggleExpand && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(task._id);
              }}
              sx={{ p: 0.5 }}
            >
              {isExpanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>

        <Box
          className="drag-handle"
          sx={{
            width: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: isSelected ? 1 : 0.3,
            transition: "opacity 0.2s ease",
            cursor: isSelected ? "grab" : "default",
            mr: 1,
            color: isSelected
              ? theme.palette.primary.main
              : theme.palette.text.disabled,
          }}
          {...(isSelected ? { ...attributes, ...listeners } : {})}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <Box
          sx={{
            width: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            disabled={isSelectionDisabled && !isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelection(task._id, e.target.checked);
            }}
            style={{
              width: 16,
              height: 16,
              cursor:
                isSelectionDisabled && !isSelected ? "not-allowed" : "pointer",
              opacity: isSelectionDisabled && !isSelected ? 0.5 : 1,
            }}
          />
        </Box>

        {isSelected && selectedCount > 1 && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              backgroundColor: theme.palette.primary.main,
              color: "white",
              borderRadius: "50%",
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: "bold",
            }}
          >
            {selectedCount}
          </Box>
        )}

        <Box sx={{ flex: 2, minWidth: 300, mr: 2 }}>
          <Tooltip
            title={displayText}
            placement="top-start"
            arrow
            enterDelay={500}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(task._id);
              }}
            >
              {type && (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    mr: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: typeInfo?.bgcolor,
                    borderRadius: "3px",
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  {typeInfo.icon}
                </Box>
              )}
              <Tooltip title={displayText} placement="top-start">
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    minWidth: 0,
                  }}
                >
                  {task?.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {task.bugKey}:
                    </span>
                  )}
                  {task.summary}
                </Typography>
              </Tooltip>

              {task.epic && (
                <Tooltip
                  title={t(
                    `EPIC ${task.epic.bugKey + " " + task.epic.summary}`
                  )}
                >
                  <Chip
                    label={`${task.epic.bugKey || task.epic.summary}`}
                    size="small"
                    icon={<BoltIcon sx={{ fontSize: "14px !important" }} />}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}08`,
                      fontWeight: 500,
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  />
                </Tooltip>
              )}

              {hasEpicTasks && (
                <Tooltip title={t("Linked tasks")}>
                  <Chip
                    label={task.epicTasks!.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      fontWeight: 600,
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  />
                </Tooltip>
              )}

              {hasSubTasks && (
                <Tooltip title={t("Sub-tasks")}>
                  <Chip
                    label={task.subTasks!.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      fontWeight: 600,
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Tooltip>
        </Box>

        <Box sx={{ width: 200, mr: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
            {renderAssignee(task.assignTo, task._id)}
          </Box>
        </Box>

        <Box sx={{ width: 120, mr: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: onStatusClick ? "pointer" : "default",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              "&:hover": onStatusClick
                ? {
                    backgroundColor: theme.palette.action.hover,
                  }
                : {},
              position: "relative",
            }}
            onClick={
              onStatusClick
                ? (e) => {
                    e.stopPropagation();
                    onStatusClick(e, task._id);
                  }
                : undefined
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {task.bugStatus?.status}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: 60 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, task._id);
            }}
            sx={{
              opacity: 0.7,
              "&:hover": {
                opacity: 1,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {isExpanded && hasExpandableContent && (
        <SubTasksDetailPanel
          parentTask={task}
          onTaskClick={onTaskClick}
          renderAssignee={renderAssignee}
        />
      )}
    </>
  );
};

interface DroppableTaskContainerProps {
  droppableId: string;
  children: React.ReactNode;
  isExpanded: boolean;
  isDragging?: boolean;
}

const DroppableTaskContainer: React.FC<DroppableTaskContainerProps> = ({
  droppableId,
  children,
  isExpanded,
  isDragging = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: "container",
      containerId: droppableId,
    },
  });

  if (!isExpanded) {
    return <>{children}</>;
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: isDragging ? 60 : "auto",
        transition: "all 0.2s ease",
        backgroundColor: isOver
          ? `${theme.palette.primary.main}15`
          : "transparent",
        border: isOver
          ? `2px dashed ${theme.palette.primary.main}`
          : "2px dashed transparent",
        borderRadius: 1,
        ...(isOver && {
          boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
        }),
      }}
    >
      {children}
      {isOver && isDragging && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: "0.875rem",
            backgroundColor: `${theme.palette.primary.main}08`,
            borderRadius: 1,
            margin: 1,
          }}
        >
          {t("Drop task(s) here")}
        </Box>
      )}
    </Box>
  );
};

const TaskAccordion: React.FC<TaskAccordionProps> = ({
  accordionKey,
  droppableId,
  title,
  isExpanded,
  onAccordionChange,
  tasks,
  totalCount,
  isLoading,
  paginationModel,
  setPaginationModel,
  onTaskClick,
  onMenuClick,
  isCreatingTask,
  newTaskName,
  onCreateTaskClick,
  onTaskNameChange,
  onCreateTask,
  onCancelTaskCreation,
  onKeyPress,
  isCreatingBug,
  headerButtons,
  sprint,
  onDeleteSprint,
  onEditSprint,
  isDeletingSprint = false,
  emptyMessage,
  isDragging = false,
  selectedTasks,
  onSelectionChange,
  isSelectionDisabled = false,
  onStatusClick,
  editingStatusTaskId,
  statusList,
  isUpdatingStatus = false,
  availableUsers = [],
  onTaskUpdate,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const textFieldRef = useRef<HTMLInputElement>(null);

  const { projectDetails, companyMemberData } = useAppSelector(selectApiData);

  const [sprintMenuAnchorEl, setSprintMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  // Assignee popover state
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
    taskId: string;
  } | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [optimisticUpdates, setOptimisticUpdates] = useState<{
    [taskId: string]: Partial<BugItem>;
  }>({});

  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isCreatingTask && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isCreatingTask]);

  useEffect(() => {
    if (isDragging) {
      setExpandedTasks(new Set());
    }
  }, [isDragging]);

  // Get project filtered users
  const getProjectFilteredUsers = useCallback(() => {
    const allCompanyUsers = companyMemberData;

    if (!allCompanyUsers || !Array.isArray(allCompanyUsers)) return [];

    if (!projectDetails?.users) return allCompanyUsers;

    const projectUserIds = projectDetails.users.map((user: any) => {
      if (typeof user === "object") {
        return user?.userId;
      }
      return user;
    });

    const projectOwnerId = projectDetails.owner;

    const ownerUser = allCompanyUsers.find(
      (member: any) => member._id === projectOwnerId
    );
    const ownerUserId = ownerUser?.userId;

    const allUserIds =
      ownerUserId && !projectUserIds.includes(ownerUserId)
        ? [...projectUserIds, ownerUserId]
        : projectUserIds;

    const result = allCompanyUsers.filter((member: any) =>
      allUserIds.includes(member.userId)
    );

    return result;
  }, [companyMemberData, projectDetails]);

  const getTaskWithOptimisticUpdates = useCallback(
    (task: BugItem): BugItem => {
      const updates = optimisticUpdates[task._id];
      if (!updates) return task;
      return { ...task, ...updates };
    },
    [optimisticUpdates]
  );

  const handleAssigneeClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, taskId: string) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setAssigneeAnchorEl({
        element: event.currentTarget,
        position: {
          top: rect.bottom,
          left: rect.left,
        },
        taskId,
      });
    },
    []
  );

  const handleAssigneeClose = () => {
    setAssigneeAnchorEl(null);
    setSearchQuery("");
  };

  const handleAssigneeToggle = async (taskId: string, user: any) => {
    const task = tasks.find((t) => t._id === taskId);
    if (!task || !onTaskUpdate) return;

    const currentTask = getTaskWithOptimisticUpdates(task);
    const currentAssignees = currentTask.assignTo || [];
    const isAssigned = currentAssignees.some((u: any) => u._id === user._id);

    const newAssignees = isAssigned
      ? currentAssignees.filter((u: any) => u._id !== user._id)
      : [...currentAssignees, { _id: user._id, fullName: user.fullName }];

    setOptimisticUpdates((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        assignTo: newAssignees,
      },
    }));

    const updatedTask = {
      ...task,
      assignTo: newAssignees,
    };

    try {
      // Pass accordionKey as containerId to identify which list to refresh
      await onTaskUpdate(taskId, updatedTask, "assignTo", accordionKey);
      setAssigneeAnchorEl(null);
      // setOptimisticUpdates((prev) => {
      //   const { [taskId]: _, ...rest } = prev;
      //   return rest;
      // });
    } catch (error) {
      setAssigneeAnchorEl(null);
      // setOptimisticUpdates((prev) => {
      //   const { [taskId]: _, ...rest } = prev;
      //   return rest;
      // });
      console.error("Error updating assignees:", error);
    }
  };

  const handleSprintMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSprintMenuAnchorEl(event.currentTarget);
  };

  const handleSprintMenuClose = () => {
    setSprintMenuAnchorEl(null);
  };

  const handleDeleteSprintClick = () => {
    if (sprint && onDeleteSprint) {
      onDeleteSprint(sprint._id);
    }
    handleSprintMenuClose();
  };

  const handleEditSprintClick = () => {
    if (sprint && onEditSprint) {
      onEditSprint(sprint._id);
    }
    handleSprintMenuClose();
  };

  const handleToggleSelection = (taskId: string, selected: boolean) => {
    if (isSelectionDisabled && !selectedTasks.has(taskId)) {
      return;
    }

    const newSelection = new Set(selectedTasks);
    if (selected) {
      newSelection.add(taskId);
    } else {
      newSelection.delete(taskId);
    }
    onSelectionChange(newSelection);
  };

  const handleToggleExpand = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const getSprintStatusDisplay = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return {
          label: "Active",
          color: "success" as const,
          variant: "filled" as const,
          icon: undefined,
          sx: {},
        };
      case "ARCHIVE":
        return {
          label: "Completed",
          color: "default" as const,
          variant: "outlined" as const,
          icon: <CheckCircleOutlineIcon sx={{ fontSize: "0.875rem" }} />,
          sx: {
            borderColor: "#4caf50",
            color: "#4caf50",
            backgroundColor: "rgba(76, 175, 80, 0.08)",
            "& .MuiChip-icon": {
              color: "#4caf50",
            },
          },
        };
      case "DRAFT":
      case "IN_ACTIVE":
        return {
          label: status === "DRAFT" ? "Draft" : "Inactive",
          color: "default" as const,
          variant: "outlined" as const,
          icon: undefined,
          sx: {},
        };
      default:
        return {
          label: status,
          color: "default" as const,
          variant: "outlined" as const,
          icon: undefined,
          sx: {},
        };
    }
  };

  const renderAssignee = useCallback(
    (assignees: any[], taskId: string) => {
      const task = tasks.find((t) => t._id === taskId);
      const optimisticTask = task ? getTaskWithOptimisticUpdates(task) : null;
      const displayAssignees = optimisticTask?.assignTo || assignees;

      if (displayAssignees && displayAssignees.length > 0) {
        return (
          <Box
            onClick={(e) => handleAssigneeClick(e, taskId)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              height: "100%",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <AvatarGroup
              max={3}
              spacing="medium"
              sx={{
                "& .MuiAvatar-root": {
                  width: 24,
                  height: 24,
                  fontSize: 12,
                },
              }}
            >
              {displayAssignees.map((user: any, index: number) => (
                <Tooltip key={user._id} title={user.fullName}>
                  <Avatar
                    sx={{
                      bgcolor: colorPalette[index % colorPalette.length],
                    }}
                  >
                    {user.fullName?.charAt(0)}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        );
      } else {
        return (
          <Box
            onClick={(e) => handleAssigneeClick(e, taskId)}
            sx={{
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              "&:hover": { color: "primary.main" },
            }}
          >
            <Person fontSize="small" />
            <Typography variant="body2">{t("Unassigned")}</Typography>
          </Box>
        );
      }
    },
    [tasks, getTaskWithOptimisticUpdates, t, handleAssigneeClick]
  );

  const currentTaskForAssigneeWithUpdates = assigneeAnchorEl
    ? getTaskWithOptimisticUpdates(
        tasks.find((t) => t._id === assigneeAnchorEl.taskId)!
      )
    : null;

  const filteredUsers = getProjectFilteredUsers()?.filter((user: any) =>
    user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderCreateTaskRow = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 52,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ width: 32, mr: 0.5 }} />
        <Box sx={{ width: 24, mr: 1 }} />
        <Box sx={{ width: 32 }} />
        <Box
          component="span"
          sx={{
            width: 16,
            height: 16,
            mr: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: typeToIconMapping.STORY.bgcolor,
            borderRadius: "3px",
            color: "white",
            flexShrink: 0,
          }}
        >
          {typeToIconMapping.STORY.icon}
        </Box>
        <TextField
          ref={textFieldRef}
          value={newTaskName}
          onChange={(e) => onTaskNameChange(e.target.value)}
          onKeyDown={onKeyPress}
          onBlur={onCreateTask}
          placeholder={t("Enter task name")}
          variant="standard"
          size="small"
          disabled={isCreatingBug}
          autoFocus
          inputProps={{
            "data-testid": `create-task-${accordionKey}`,
          }}
          sx={{
            flex: 1,
            "& .MuiInput-root": {
              "&:before": {
                borderBottom: "1px solid transparent",
              },
              "&:after": {
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              },
            },
          }}
          InputProps={{
            disableUnderline: false,
            endAdornment: isCreatingBug && (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  {t("Creating...")}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  };

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <Box>
      {[...Array(5)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: 32, mr: 0.5 }}>
            <Skeleton variant="rectangular" width={16} height={16} />
          </Box>
          <Box sx={{ width: 24, mr: 1 }}>
            <Skeleton variant="rectangular" width={16} height={16} />
          </Box>
          <Box sx={{ width: 32 }}>
            <Skeleton variant="rectangular" width={16} height={16} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Skeleton
              variant="rectangular"
              width={16}
              height={16}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width="40%" />
          </Box>
          <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={60} />
        </Box>
      ))}
    </Box>
  );

  const isAllSelected = tasks.length > 0 && selectedTasks.size === tasks.length;
  const isPartiallySelected =
    selectedTasks.size > 0 && selectedTasks.size < tasks.length;

  const formatSprintDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const startDay = start.getDate();
    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endDay = end.getDate();
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });

    if (start.getMonth() === end.getMonth()) {
      return `${startDay} - ${endDay} ${endMonth}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
  };

  const statusDisplay = sprint ? getSprintStatusDisplay(sprint.status) : null;

  return (
    <Paper sx={{ mb: 2, overflow: "hidden" }}>
      <Accordion
        expanded={isExpanded}
        onChange={onAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 48,
            "& .MuiAccordionSummary-content": {
              margin: "8px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.secondary"
            >
              {title}
            </Typography>
            {isExpanded && (
              <Typography variant="body2" color="text.secondary">
                ({totalCount})
              </Typography>
            )}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
            >
              {sprint && statusDisplay && (
                <Chip
                  label={statusDisplay.label}
                  size="small"
                  color={statusDisplay.color}
                  variant={statusDisplay.variant}
                  icon={statusDisplay.icon}
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                    ...statusDisplay.sx,
                  }}
                />
              )}
            </Box>

            {sprint && (
              <>
                {/* <Chip
                  label={sprint.status}
                  size="small"
                  color={sprint.status === "ACTIVE" ? "primary" : "default"}
                  sx={{
                    fontWeight: sprint.status === "ACTIVE" ? "bold" : "normal",
                  }}
                  variant={sprint.status === "ACTIVE" ? "filled" : "outlined"}
                /> */}
                {sprint.startDate && sprint.endDate ? (
                  <Chip
                    label={formatSprintDateRange(
                      sprint.startDate,
                      sprint.endDate
                    )}
                    size="small"
                    variant="outlined"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  />
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEditSprint) {
                        onEditSprint(sprint._id);
                      }
                    }}
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}08`,
                      },
                    }}
                  >
                    {t("Add dates")}
                  </Button>
                )}
              </>
            )}
            {selectedTasks.size > 0 && (
              <Chip
                label={`${selectedTasks.size} selected`}
                size="small"
                sx={{ fontWeight: "bold" }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {headerButtons &&
              headerButtons.map((button, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  startIcon={button.icon}
                  onClick={(e) => {
                    e.stopPropagation();
                    button.onClick(e);
                  }}
                  disabled={button.disabled}
                  sx={{
                    textTransform: "none",
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.main}08`,
                    },
                  }}
                >
                  {button.label}
                </Button>
              ))}

            {sprint && onDeleteSprint && (
              <IconButton
                size="small"
                onClick={handleSprintMenuClick}
                disabled={isDeletingSprint}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <DroppableTaskContainer
            droppableId={droppableId}
            isExpanded={isExpanded}
            isDragging={isDragging}
          >
            {isLoading ? (
              renderLoadingSkeleton()
            ) : (
              <Box>
                {tasks.length === 0 && !isCreatingTask ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage || t("No tasks found")}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {isCreatingTask && renderCreateTaskRow()}

                    {tasks?.map((task) => (
                      <DraggableTaskRow
                        key={task._id}
                        task={task}
                        onTaskClick={onTaskClick}
                        onMenuClick={onMenuClick}
                        isDragging={isDragging}
                        isSelected={selectedTasks.has(task._id)}
                        selectedCount={selectedTasks.size}
                        onToggleSelection={handleToggleSelection}
                        isSelectionDisabled={isSelectionDisabled}
                        onStatusClick={onStatusClick}
                        isUpdatingStatus={isUpdatingStatus}
                        isExpanded={expandedTasks.has(task._id)}
                        onToggleExpand={handleToggleExpand}
                        renderAssignee={renderAssignee}
                      />
                    ))}

                    {tasks.length > 0 && (
                      <Box
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          p: 1,
                        }}
                      >
                        <Pagination
                          total={totalCount}
                          paginationModel={paginationModel}
                          setPaginationModel={setPaginationModel}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DroppableTaskContainer>

          {isExpanded && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[25],
                p: 1,
              }}
            >
              {isCreatingTask ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 1 }}
                >
                  {t("Press Enter to save or Escape to cancel")}
                </Typography>
              ) : (
                accordionKey !== "backlog" && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onCreateTaskClick}
                    disabled={isCreatingBug}
                    sx={{
                      textTransform: "none",
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.primary.main,
                        backgroundColor: `${theme.palette.primary.main}08`,
                      },
                    }}
                  >
                    {t("Create")}
                  </Button>
                )
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Menu
        anchorEl={sprintMenuAnchorEl}
        open={Boolean(sprintMenuAnchorEl)}
        onClose={handleSprintMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditSprintClick} disabled={isDeletingSprint}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">{t("Edit Sprint")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteSprintClick}
          disabled={isDeletingSprint}
          sx={{
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light + "20",
            },
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">
            {isDeletingSprint ? t("Deleting...") : t("Delete Sprint")}
          </Typography>
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(assigneeAnchorEl)}
        anchorEl={assigneeAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={assigneeAnchorEl?.position}
        onClose={handleAssigneeClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            p: 1,
          },
        }}
      >
        <Box sx={{ p: 1, mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Assigned")}
          </Typography>
          {currentTaskForAssigneeWithUpdates?.assignTo &&
          currentTaskForAssigneeWithUpdates.assignTo.length > 0 ? (
            currentTaskForAssigneeWithUpdates.assignTo
              .filter((user: any) =>
                user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((user: any, index: number) => (
                <MenuItem
                  key={user._id}
                  onClick={() =>
                    assigneeAnchorEl &&
                    handleAssigneeToggle(assigneeAnchorEl.taskId, user)
                  }
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        background:
                          colorPalette[
                            currentTaskForAssigneeWithUpdates.assignTo.findIndex(
                              (u: any) => u._id === user._id
                            ) % colorPalette.length
                          ],
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                        {user.fullName?.charAt(0)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                      {user.fullName}
                    </Typography>
                    <Checkbox checked={true} size="small" />
                  </Box>
                </MenuItem>
              ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {t("No users assigned")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Suggestions")}
          </Typography>
          {filteredUsers &&
          filteredUsers.filter(
            (user: any) =>
              !currentTaskForAssigneeWithUpdates?.assignTo?.some(
                (u: any) => u._id === user._id
              )
          ).length > 0 ? (
            filteredUsers
              .filter(
                (user: any) =>
                  !currentTaskForAssigneeWithUpdates?.assignTo?.some(
                    (u: any) => u._id === user._id
                  )
              )
              .map((user: any, index: number) => (
                <MenuItem
                  key={user._id}
                  onClick={() =>
                    assigneeAnchorEl &&
                    handleAssigneeToggle(assigneeAnchorEl.taskId, user)
                  }
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        background: colorPalette[index % colorPalette.length],
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                        {user.fullName?.charAt(0)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                      {user.fullName}
                    </Typography>
                    <Checkbox checked={false} size="small" />
                  </Box>
                </MenuItem>
              ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {searchQuery
                ? t("No users found")
                : t("No additional users available")}
            </Typography>
          )}
        </Box>
      </Popover>
    </Paper>
  );
};

export default TaskAccordion;
